import { useSelector } from 'react-redux';
import { useMemo, useCallback } from 'react';
import { adminApiService } from '../services/adminApiService';
import type { ApiResponse, AdminCustomer, ComplianceAlert, TransactionCounts, StablecoinConfigItem } from '../services/adminApiService';

export function useAdminApiService() {
  const token = useSelector((state: any) => state.auth.token);
  const authType = useSelector((state: any) => state.auth.type);

  // Create a stable token value that only changes when token or authType actually change
  const stableToken = useMemo(() => {
    if (authType === 'JWT' && token && token !== 'null' && token !== 'undefined') {
      return token;
    }
    return undefined; // For session auth, don't pass token
  }, [token, authType]);

  // Memoize each function individually to ensure stability
  const getRealCustomersWithAccounts = useCallback((page = 0, size = 100, sortBy?: string, sortDirection?: string) =>
    adminApiService.getRealCustomersWithAccounts(page, size, stableToken, sortBy, sortDirection), [stableToken]);

  const reactivateCustomer = useCallback((customerId: number, reason: string) =>
    adminApiService.reactivateCustomer(customerId, reason, stableToken), [stableToken]);

  const getCustomerById = useCallback((customerId: number) =>
    adminApiService.getCustomerById(customerId, stableToken), [stableToken]);

  const getDashboardStats = useCallback(() => 
    adminApiService.getDashboardStats(stableToken), [stableToken]);

  const getPortfolioStats = useCallback(() => 
    adminApiService.getPortfolioStats(stableToken), [stableToken]);

  const getKucoinBalances = useCallback((accountId?: number | null) => 
    adminApiService.getKucoinBalances(accountId, stableToken), [stableToken]);

  const getKucoinAccountOverview = useCallback((currency: string = 'USDT', accountId?: number | null) => 
    adminApiService.getKucoinAccountOverview(currency, accountId, stableToken), [stableToken]);

  const getActiveTradingBots = useCallback(() => 
    adminApiService.getActiveTradingBots(stableToken), [stableToken]);

  const saveKucoinSnapshot = useCallback(() => 
    adminApiService.saveKucoinSnapshot(stableToken), [stableToken]);

  const getPrimaryKucoinConfigId = useCallback(() =>
    adminApiService.getPrimaryKucoinConfigId(stableToken), [stableToken]);

  const getKucoinSnapshots = useCallback((startDate: string, endDate: string, accountId?: number | null) =>
    adminApiService.getKucoinSnapshots(startDate, endDate, accountId, stableToken), [stableToken]);

  const getKucoinSnapshotByDate = useCallback((date: string) => 
    adminApiService.getKucoinSnapshotByDate(date, stableToken), [stableToken]);

  const getLatestKucoinSnapshot = useCallback(() => 
    adminApiService.getLatestKucoinSnapshot(stableToken), [stableToken]);

  const getKucoinFundingHistory = useCallback((currency: string = 'USDT') => 
    adminApiService.getKucoinFundingHistory(currency, stableToken), [stableToken]);

  const getKucoinFuturesHistoryPositions = useCallback((limit: number = 200, accountId?: number | null) => 
    adminApiService.getKucoinFuturesHistoryPositions(limit, accountId, stableToken), [stableToken]);

  const getAllAccounts = useCallback((page = 0, size = 100, searchTerm?: string, status?: string, type?: string, sortBy?: string, sortDirection?: string) =>
    adminApiService.getAllAccounts(page, size, searchTerm, status, type, sortBy, sortDirection, stableToken), [stableToken]);

  const createCurrencyAccount = useCallback((customerId: number, accountType: string, currency: string) =>
    adminApiService.createCurrencyAccount(customerId, accountType, currency, stableToken), [stableToken]);

  const getAllAdminUsers = useCallback(() => 
    adminApiService.getAllAdminUsers(stableToken), [stableToken]);

  const createAdminUser = useCallback((userData: any) => 
    adminApiService.createAdminUser(userData, stableToken), [stableToken]);

  const toggleAdminUserStatus = useCallback((userId: string) => 
    adminApiService.toggleAdminUserStatus(userId, stableToken), [stableToken]);

  const getAdminProfile = useCallback(() => 
    adminApiService.getAdminProfile(stableToken), [stableToken]);

  const uploadAdminAvatar = useCallback((file: File) => 
    adminApiService.uploadAdminAvatar(file, stableToken), [stableToken]);

  const changeAdminPassword = useCallback((oldPassword: string, newPassword: string) => 
    adminApiService.changeAdminPassword(oldPassword, newPassword, stableToken), [stableToken]);

  const getTransactions = useCallback((filter: any) => 
    adminApiService.getTransactions(filter, stableToken), [stableToken]);

  const approveTransaction = useCallback((transactionId: number) => 
    adminApiService.approveTransaction(transactionId, stableToken), [stableToken]);

  const rejectTransaction = useCallback((transactionId: number, reason: string) => 
    adminApiService.rejectTransaction(transactionId, reason, stableToken), [stableToken]);

  const getAuditLogs = useCallback((
    page: number = 0,
    size: number = 50,
    filters?: {
      action?: string;
      actionPrefix?: string;
      entityType?: string;
      entityId?: number;
      aiTuneOnly?: boolean;
      marketSignalOnly?: boolean;
    }
  ) => adminApiService.getAuditLogs(page, size, filters, stableToken), [stableToken]);

  const getIpBlocklist = useCallback(() =>
    adminApiService.getIpBlocklist(stableToken), [stableToken]);

  const addIpToBlocklist = useCallback((
    ipAddress: string,
    reason?: string,
    entryType: 'IP' | 'EMAIL' | 'PHONE' = 'IP'
  ) =>
    adminApiService.addIpToBlocklist(ipAddress, reason, stableToken, entryType), [stableToken]);

  const setIpBlocklistEnabled = useCallback((id: number, enabled: boolean) =>
    adminApiService.setIpBlocklistEnabled(id, enabled, stableToken), [stableToken]);

  const deleteIpFromBlocklist = useCallback((id: number) =>
    adminApiService.deleteIpFromBlocklist(id, stableToken), [stableToken]);

  const getPlatformCoins = useCallback(() =>
    adminApiService.getPlatformCoins(stableToken), [stableToken]);

  const createPlatformCoin = useCallback((coinData: any) =>
    adminApiService.createPlatformCoin(coinData, stableToken), [stableToken]);

  const updatePlatformCoin = useCallback((id: number, coinData: any) =>
    adminApiService.updatePlatformCoin(id, coinData, stableToken), [stableToken]);

  const getAirdropCampaigns = useCallback((platformCoinId?: number) =>
    adminApiService.getAirdropCampaigns(platformCoinId, stableToken), [stableToken]);

  const getAirdropCampaign = useCallback((id: number, includeRecipients: boolean = true) =>
    adminApiService.getAirdropCampaign(id, includeRecipients, stableToken), [stableToken]);

  const createAirdropCampaign = useCallback((campaignData: any) =>
    adminApiService.createAirdropCampaign(campaignData, stableToken), [stableToken]);

  const previewAirdropCampaign = useCallback((id: number) =>
    adminApiService.previewAirdropCampaign(id, stableToken), [stableToken]);

  const dryRunAirdropCampaign = useCallback((id: number) =>
    adminApiService.dryRunAirdropCampaign(id, stableToken), [stableToken]);

  const cancelAirdropCampaign = useCallback((id: number) =>
    adminApiService.cancelAirdropCampaign(id, stableToken), [stableToken]);

  const getAirdropExecuteConfig = useCallback(() =>
    adminApiService.getAirdropExecuteConfig(stableToken), [stableToken]);

  const executeAirdropCampaign = useCallback(
    (id: number, body: { fundingWalletId: number; confirmLiveSend: boolean }) =>
      adminApiService.executeAirdropCampaign(id, body, stableToken),
    [stableToken]
  );

  const listAdminCryptoWallets = useCallback(() =>
    adminApiService.listAdminCryptoWallets(stableToken), [stableToken]);

  const getPlatformCoinDeployConfig = useCallback(() =>
    adminApiService.getPlatformCoinDeployConfig(stableToken), [stableToken]);

  const deployPlatformCoinTestnet = useCallback(
    (id: number, body: { fundingWalletId: number; confirmDeploy: boolean }) =>
      adminApiService.deployPlatformCoinTestnet(id, body, stableToken),
    [stableToken]
  );

  const getComplianceAlerts = useCallback((page: number = 0, size: number = 100, severity?: string, status?: string) =>
    adminApiService.getComplianceAlerts(page, size, severity, status, stableToken), [stableToken]);

  const assignAlert = useCallback((alertId: string, assigneeId: string) =>
    adminApiService.assignAlert(alertId, assigneeId, stableToken), [stableToken]);

  const updateAlertStatus = useCallback((alertId: string, status: string, notes?: string) =>
    adminApiService.updateAlertStatus(alertId, status, notes, stableToken), [stableToken]);

  const resolveComplianceAlert = useCallback((alertId: string, resolution: 'APPROVED' | 'REJECTED', actionTaken: string) =>
    adminApiService.resolveComplianceAlert(alertId, resolution, actionTaken, stableToken), [stableToken]);

  const getProviderConfigs = useCallback((page: number = 0, size: number = 10) => 
    adminApiService.getProviderConfigs(page, size, stableToken), [stableToken]);

  const getProviderConfigsByProvider = useCallback((provider: string, page: number = 0, size: number = 10) => 
    adminApiService.getProviderConfigsByProvider(provider, page, size, stableToken), [stableToken]);

  const updateProviderConfig = useCallback((provider: string, configKey: string, configValue: string) =>
    adminApiService.updateProviderConfig(provider, configKey, configValue, stableToken), [stableToken]);

  const getTradingProviderStatus = useCallback(() =>
    adminApiService.getTradingProviderStatus(stableToken), [stableToken]);

  const getCurrentTradingProvider = useCallback(() =>
    adminApiService.getCurrentTradingProvider(stableToken), [stableToken]);

  const setTradingProvider = useCallback((provider: string, validateOnly: boolean = false) =>
    adminApiService.setTradingProvider(provider, validateOnly, stableToken), [stableToken]);

  const testTradingProviderConnection = useCallback((provider: string) =>
    adminApiService.testTradingProviderConnection(provider, stableToken), [stableToken]);

  const getAssetTokens = useCallback(() =>
    adminApiService.getAssetTokens(stableToken), [stableToken]);

  const createAssetToken = useCallback((tokenData: any) =>
    adminApiService.createAssetToken(tokenData, stableToken), [stableToken]);

  const updateAssetTokenValue = useCallback((tokenId: number, newValue: number) =>
    adminApiService.updateAssetTokenValue(tokenId, newValue, stableToken), [stableToken]);

  const updateAssetTokenStatus = useCallback((tokenId: number, status: string) =>
    adminApiService.updateAssetTokenStatus(tokenId, status, stableToken), [stableToken]);

  const getSessionSettings = useCallback(() =>
    adminApiService.getSessionSettings(stableToken), [stableToken]);

  const updateSessionSettings = useCallback((settings: any) =>
    adminApiService.updateSessionSettings(settings, stableToken), [stableToken]);

  // Multi-signature wallet methods
  const getMultiSignatureWallets = useCallback((walletType?: string) =>
    adminApiService.getMultiSignatureWallets(walletType, stableToken), [stableToken]);

  const getMultiSignatureWalletById = useCallback((walletId: number) =>
    adminApiService.getMultiSignatureWalletById(walletId, stableToken), [stableToken]);

  const createMultiSignatureWallet = useCallback((walletData: any) =>
    adminApiService.createMultiSignatureWallet(walletData, stableToken), [stableToken]);

  const createPropertyEscrowWallet = useCallback((walletData: any) =>
    adminApiService.createPropertyEscrowWallet(walletData, stableToken), [stableToken]);

  const activateMultiSignatureWallet = useCallback((walletId: number) =>
    adminApiService.activateMultiSignatureWallet(walletId, stableToken), [stableToken]);

  const freezeMultiSignatureWallet = useCallback((walletId: number) =>
    adminApiService.freezeMultiSignatureWallet(walletId, stableToken), [stableToken]);

  const getWalletSignatories = useCallback((walletId: number) =>
    adminApiService.getWalletSignatories(walletId, stableToken), [stableToken]);

  const getWalletTransactions = useCallback((walletId: number) =>
    adminApiService.getWalletTransactions(walletId, stableToken), [stableToken]);

  const createWalletTransaction = useCallback((walletId: number, transactionData: any) =>
    adminApiService.createWalletTransaction(walletId, transactionData, stableToken), [stableToken]);

  const getAvailableUsers = useCallback(() =>
    adminApiService.getAvailableUsers(stableToken), [stableToken]);

  const getInactivitySettings = useCallback(() =>
    adminApiService.getInactivitySettings(stableToken), [stableToken]);

  const updateInactivitySettings = useCallback((settings: any) =>
    adminApiService.updateInactivitySettings(settings, stableToken), [stableToken]);

  const getSecuritySettings = useCallback(() =>
    adminApiService.getSecuritySettings(stableToken), [stableToken]);

  const updateSecuritySettings = useCallback((settings: any) =>
    adminApiService.updateSecuritySettings(settings, stableToken), [stableToken]);

  const getTransactionCounts = useCallback((): Promise<ApiResponse<TransactionCounts>> =>
    adminApiService.getTransactionCounts(), []);

  const getKycSummary = useCallback(() =>
    adminApiService.getKycSummary(), []);

  const getKybSummary = useCallback(() =>
    adminApiService.getKybSummary(), []);

  // Reports/Compliance helpers
  const getKycSubmissions = useCallback((page: number = 0, size: number = 1, status?: string, productId?: number) =>
    adminApiService.getKycSubmissions(page, size, status, productId, stableToken), [stableToken]);

  const getKybSubmissions = useCallback((page: number = 0, size: number = 1, status?: string, productId?: number) =>
    adminApiService.getKybSubmissions(page, size, status, productId, stableToken), [stableToken]);

  const reviewKycSubmission = useCallback((submissionId: number, status: string, comments?: string) =>
    adminApiService.reviewKycSubmission(submissionId, status, comments, stableToken), [stableToken]);

  const reviewKybSubmission = useCallback((submissionId: number, status: string, comments?: string) =>
    adminApiService.reviewKybSubmission(submissionId, status, comments, stableToken), [stableToken]);

  const getAvailableProducts = useCallback(() =>
    adminApiService.getAvailableProducts(stableToken), [stableToken]);

  // Trading Strategy Reports
  const getTradingStrategyDetailedTransactions = useCallback(() =>
    adminApiService.getTradingStrategyDetailedTransactions(stableToken), [stableToken]);

  const getTradingStrategySummary = useCallback(() =>
    adminApiService.getTradingStrategySummary(stableToken), [stableToken]);

  const getTradingStrategyPnlSummary = useCallback(() =>
    adminApiService.getTradingStrategyPnlSummary(stableToken), [stableToken]);

  const getTradingStrategyPnlHistory = useCallback((daysBack: number = 30) =>
    adminApiService.getTradingStrategyPnlHistory(daysBack, stableToken), [stableToken]);

  const getTradingStrategyComparison = useCallback(() =>
    adminApiService.getTradingStrategyComparison(stableToken), [stableToken]);

  const getTradingStrategyRecentTransactions = useCallback((days: number = 30) =>
    adminApiService.getTradingStrategyRecentTransactions(days, stableToken), [stableToken]);

  const getTradingStrategyDetailedTransactionsWithUsers = useCallback(() =>
    adminApiService.getTradingStrategyDetailedTransactionsWithUsers(stableToken), [stableToken]);

  const getTradingStrategyVolatilityData = useCallback((marketType: string = 'FUTURES') =>
    adminApiService.getTradingStrategyVolatilityData(marketType, stableToken), [stableToken]);

  const getAllTradingStrategies = useCallback(() =>
    adminApiService.getAllTradingStrategies(stableToken), [stableToken]);

  // Reconciliation Methods
  const getStrategyReconciliationData = useCallback((strategyId: number) =>
    adminApiService.getStrategyReconciliationData(strategyId, stableToken), [stableToken]);

  const reconcileStrategyWithKuCoin = useCallback((strategyId: number) =>
    adminApiService.reconcileStrategyWithKuCoin(strategyId, stableToken), [stableToken]);

  const fixReconciliationDiscrepancies = useCallback((discrepancies: any[]) =>
    adminApiService.fixReconciliationDiscrepancies(discrepancies, stableToken), [stableToken]);

  const backfillMissingCloseOrderIds = useCallback((strategyId: number) =>
    adminApiService.backfillMissingCloseOrderIds(strategyId, stableToken), [stableToken]);

  const syncClosedTradesFromKuCoinHistory = useCallback((strategyId: number, importMissing: boolean = false) =>
    adminApiService.syncClosedTradesFromKuCoinHistory(strategyId, importMissing, stableToken), [stableToken]);

  const fixHedgePositions = useCallback((strategyId: number) =>
    adminApiService.fixHedgePositions(strategyId, stableToken), [stableToken]);

  // Live Data
  const getLiveData = useCallback((marketType: string = 'FUTURES') =>
    adminApiService.getLiveData(marketType, stableToken), [stableToken]);

  // Hedge-based Live Data
  const getHedgeLiveData = useCallback((marketType: string = 'FUTURES') =>
    adminApiService.getHedgeLiveData(marketType, stableToken), [stableToken]);

  // Hedge positions (per-leg) for a strategy
  const getHedgePositionsForStrategy = useCallback((strategyId: number) =>
    adminApiService.getHedgePositionsForStrategy(strategyId, stableToken), [stableToken]);

  // Strategy Details
  const getStrategyDetails = useCallback((strategyId: number) =>
    adminApiService.getStrategyDetails(strategyId, stableToken), [stableToken]);

  // CSV Export Methods
  const exportTradingStrategyDetailedTransactionsCSV = useCallback(() =>
    adminApiService.exportTradingStrategyDetailedTransactionsCSV(stableToken), [stableToken]);

  const exportTradingStrategySummaryCSV = useCallback(() =>
    adminApiService.exportTradingStrategySummaryCSV(stableToken), [stableToken]);

  const exportTradingStrategyPnlSummaryCSV = useCallback(() =>
    adminApiService.exportTradingStrategyPnlSummaryCSV(stableToken), [stableToken]);

  const exportTradingStrategyComparisonCSV = useCallback(() =>
    adminApiService.exportTradingStrategyComparisonCSV(stableToken), [stableToken]);

  // PDF Export Methods
  const exportTradingStrategyDetailedTransactionsPDF = useCallback(() =>
    adminApiService.exportTradingStrategyDetailedTransactionsPDF(stableToken), [stableToken]);

  const exportTradingStrategySummaryPDF = useCallback(() =>
    adminApiService.exportTradingStrategySummaryPDF(stableToken), [stableToken]);

  const exportTradingStrategyPnlSummaryPDF = useCallback(() =>
    adminApiService.exportTradingStrategyPnlSummaryPDF(stableToken), [stableToken]);

  const exportTradingStrategyComparisonPDF = useCallback(() =>
    adminApiService.exportTradingStrategyComparisonPDF(stableToken), [stableToken]);

  const exportTradingStrategyDetailedTransactionsWithUsersCSV = useCallback(() =>
    adminApiService.exportTradingStrategyDetailedTransactionsWithUsersCSV(stableToken), [stableToken]);

  const exportTradingStrategyDetailedTransactionsWithUsersPDF = useCallback(() =>
    adminApiService.exportTradingStrategyDetailedTransactionsWithUsersPDF(stableToken), [stableToken]);

  const exportTradingStrategyRecentTransactionsCSV = useCallback((days: number = 30) =>
    adminApiService.exportTradingStrategyRecentTransactionsCSV(days, stableToken), [stableToken]);

  const exportTradingStrategyRecentTransactionsPDF = useCallback((days: number = 30) =>
    adminApiService.exportTradingStrategyRecentTransactionsPDF(days, stableToken), [stableToken]);

  // Strategy Logs
  // US13: Enhanced to support filtering by symbol, side, and positionId for leg-specific logs
  const getStrategyLogs = useCallback((
    strategyName: string, 
    lines: number = 500,
    filters?: { symbol?: string; side?: 'LONG' | 'SHORT'; positionId?: string }
  ) => {
    // If filters are provided, pass them; otherwise use token for backward compatibility
    if (filters) {
      return adminApiService.getStrategyLogs(strategyName, lines, filters);
    } else {
      return adminApiService.getStrategyLogs(strategyName, lines, stableToken);
    }
  }, [stableToken]);

  // US6 / US16: Close a single hedge leg by its hedgePositionId
  const closeHedgeLeg = useCallback((legId: number, reason?: string) =>
    adminApiService.closeHedgeLeg(legId, stableToken, reason), [stableToken]);

  const closePosition = useCallback((positionId: string, symbol: string, positionType: 'LONG' | 'SHORT', reason?: string) =>
    adminApiService.closePosition(positionId, symbol, positionType, stableToken, reason), [stableToken]);

  // Admin Personal Trading Accounts (US6-US8)
  const getMyTradingAccounts = useCallback(() =>
    adminApiService.getMyTradingAccounts(stableToken), [stableToken]);

  const getMyTradingAccountById = useCallback((id: number) =>
    adminApiService.getMyTradingAccountById(id, stableToken), [stableToken]);

  const createMyTradingAccount = useCallback((payload: {
    name: string;
    apiKey: string;
    apiSecret: string;
    apiPassphrase: string;
    baseUrl?: string;
    isActive?: boolean;
    kucoinCopyTradeLeadEnabled?: boolean;
  }) =>
    adminApiService.createMyTradingAccount(payload, stableToken), [stableToken]);

  const updateMyTradingAccount = useCallback((id: number, payload: {
    name?: string;
    apiKey?: string;
    apiSecret?: string;
    apiPassphrase?: string;
    baseUrl?: string;
    isActive?: boolean;
    kucoinCopyTradeLeadEnabled?: boolean;
  }) =>
    adminApiService.updateMyTradingAccount(id, payload, stableToken), [stableToken]);

  const deleteMyTradingAccount = useCallback((id: number) =>
    adminApiService.deleteMyTradingAccount(id, stableToken), [stableToken]);

  const testMyTradingAccount = useCallback((id: number) =>
    adminApiService.testMyTradingAccount(id, stableToken), [stableToken]);

  // Stablecoin Config Management
  const getStablecoinConfigs = useCallback(() =>
    adminApiService.getStablecoinConfigs(stableToken), [stableToken]);

  const createStablecoinConfig = useCallback((dto: Partial<StablecoinConfigItem>) =>
    adminApiService.createStablecoinConfig(dto, stableToken), [stableToken]);

  const updateStablecoinConfig = useCallback((id: number, dto: Partial<StablecoinConfigItem>) =>
    adminApiService.updateStablecoinConfig(id, dto, stableToken), [stableToken]);

  const setStablecoinConfigStatus = useCallback((id: number, enabled: boolean) =>
    adminApiService.setStablecoinConfigStatus(id, enabled, stableToken), [stableToken]);

  const deleteStablecoinConfig = useCallback((id: number) =>
    adminApiService.deleteStablecoinConfig(id, stableToken), [stableToken]);

  const getAdminSubscriptionStatus = useCallback(() =>
    adminApiService.getAdminSubscriptionStatus(stableToken), [stableToken]);

  const getAdminSubscriptionPlans = useCallback(() =>
    adminApiService.getAdminSubscriptionPlans(stableToken), [stableToken]);

  const startAdminSubscriptionRenewal = useCallback((
    planId: number,
    paymentMethod: 'STRIPE' | 'CRYPTO' = 'STRIPE',
    cryptoBlockchain?: string,
    cryptoSymbol?: string
  ) =>
    adminApiService.startAdminSubscriptionRenewal(planId, paymentMethod, cryptoBlockchain, cryptoSymbol, stableToken), [stableToken]);

  const getAdminSubscriptionRenewPending = useCallback(() =>
    adminApiService.getAdminSubscriptionRenewPending(stableToken), [stableToken]);

  const confirmAdminSubscriptionRenewalPayment = useCallback((body?: { sessionId?: string }) =>
    adminApiService.confirmAdminSubscriptionRenewalPayment(body, stableToken), [stableToken]);
  const submitAdminSubscriptionCryptoPayment = useCallback((body: { renewalRequestId: number; txHash: string }) =>
    adminApiService.submitAdminSubscriptionCryptoPayment(body, stableToken), [stableToken]);
  const payAdminSubscriptionWithWallet = useCallback((body: { renewalRequestId: number }) =>
    adminApiService.payAdminSubscriptionWithWallet(body, stableToken), [stableToken]);

  // Return a stable object with all the functions
  return useMemo(() => ({
    getRealCustomersWithAccounts,
    reactivateCustomer,
    getCustomerById,
    getDashboardStats,
    getPortfolioStats,
    getKucoinBalances,
    getKucoinAccountOverview,
    getActiveTradingBots,
    saveKucoinSnapshot,
    getPrimaryKucoinConfigId,
    getKucoinSnapshots,
    getKucoinSnapshotByDate,
    getLatestKucoinSnapshot,
    getKucoinFundingHistory,
    getKucoinFuturesHistoryPositions,
    getAllAccounts,
    createCurrencyAccount,
    getAllAdminUsers,
    createAdminUser,
    toggleAdminUserStatus,
    getAdminProfile,
    uploadAdminAvatar,
    changeAdminPassword,
    getTransactions,
    approveTransaction,
    rejectTransaction,
    getAuditLogs,
    getIpBlocklist,
    addIpToBlocklist,
    setIpBlocklistEnabled,
    deleteIpFromBlocklist,
    getPlatformCoins,
    createPlatformCoin,
    updatePlatformCoin,
    getAirdropCampaigns,
    getAirdropCampaign,
    createAirdropCampaign,
    previewAirdropCampaign,
    dryRunAirdropCampaign,
    cancelAirdropCampaign,
    getAirdropExecuteConfig,
    executeAirdropCampaign,
    listAdminCryptoWallets,
    getPlatformCoinDeployConfig,
    deployPlatformCoinTestnet,
    getComplianceAlerts,
    assignAlert,
    updateAlertStatus,
    resolveComplianceAlert,
    getProviderConfigs,
    getProviderConfigsByProvider,
    updateProviderConfig,
    getTradingProviderStatus,
    getCurrentTradingProvider,
    setTradingProvider,
    testTradingProviderConnection,
    getAssetTokens,
    createAssetToken,
    updateAssetTokenValue,
    updateAssetTokenStatus,
    getSessionSettings,
    updateSessionSettings,
    getInactivitySettings,
    updateInactivitySettings,
    getSecuritySettings,
    updateSecuritySettings,
    getTransactionCounts,
    getKycSummary,
    getKybSummary,
    getKycSubmissions,
    getKybSubmissions,
    reviewKycSubmission,
    reviewKybSubmission,
    getAvailableProducts,
    // Multi-signature wallet methods
    getMultiSignatureWallets,
    getMultiSignatureWalletById,
    createMultiSignatureWallet,
    createPropertyEscrowWallet,
    activateMultiSignatureWallet,
    freezeMultiSignatureWallet,
    getWalletSignatories,
    getWalletTransactions,
    createWalletTransaction,
    getAvailableUsers,
    // Trading Strategy Reports
    getTradingStrategyDetailedTransactions,
    getTradingStrategySummary,
    getTradingStrategyPnlSummary,
    getTradingStrategyPnlHistory,
    getTradingStrategyComparison,
    getTradingStrategyRecentTransactions,
    getTradingStrategyDetailedTransactionsWithUsers,
    getTradingStrategyVolatilityData,
    getAllTradingStrategies,
    getStrategyReconciliationData,
    reconcileStrategyWithKuCoin,
    fixReconciliationDiscrepancies,
    backfillMissingCloseOrderIds,
    syncClosedTradesFromKuCoinHistory,
    fixHedgePositions,
    getLiveData,
    getHedgeLiveData,
    getHedgePositionsForStrategy,
    getStrategyDetails,
    exportTradingStrategyDetailedTransactionsCSV,
    exportTradingStrategySummaryCSV,
    exportTradingStrategyPnlSummaryCSV,
    exportTradingStrategyComparisonCSV,
    exportTradingStrategyDetailedTransactionsPDF,
    exportTradingStrategySummaryPDF,
    exportTradingStrategyPnlSummaryPDF,
    exportTradingStrategyComparisonPDF,
    exportTradingStrategyDetailedTransactionsWithUsersCSV,
    exportTradingStrategyDetailedTransactionsWithUsersPDF,
    exportTradingStrategyRecentTransactionsCSV,
    exportTradingStrategyRecentTransactionsPDF,
    getStrategyLogs,
    closeHedgeLeg,
    closePosition,
    // Admin Personal Trading Accounts
    getMyTradingAccounts,
    getMyTradingAccountById,
    createMyTradingAccount,
    updateMyTradingAccount,
    deleteMyTradingAccount,
    testMyTradingAccount,
    // Stablecoin Config Management
    getStablecoinConfigs,
    createStablecoinConfig,
    updateStablecoinConfig,
    setStablecoinConfigStatus,
    deleteStablecoinConfig,
    // Subscription
    getAdminSubscriptionStatus,
    getAdminSubscriptionPlans,
    startAdminSubscriptionRenewal,
    getAdminSubscriptionRenewPending,
    confirmAdminSubscriptionRenewalPayment,
    submitAdminSubscriptionCryptoPayment,
    payAdminSubscriptionWithWallet
  }), [
    getRealCustomersWithAccounts,
    reactivateCustomer,
    getCustomerById,
    getDashboardStats,
    getPortfolioStats,
    getKucoinBalances,
    getKucoinAccountOverview,
    getActiveTradingBots,
    saveKucoinSnapshot,
    getPrimaryKucoinConfigId,
    getKucoinSnapshots,
    getKucoinSnapshotByDate,
    getLatestKucoinSnapshot,
    getKucoinFundingHistory,
    getKucoinFuturesHistoryPositions,
    getAllAccounts,
    createCurrencyAccount,
    getAllAdminUsers,
    createAdminUser,
    toggleAdminUserStatus,
    getAdminProfile,
    uploadAdminAvatar,
    changeAdminPassword,
    getTransactions,
    approveTransaction,
    rejectTransaction,
    getAuditLogs,
    getIpBlocklist,
    addIpToBlocklist,
    setIpBlocklistEnabled,
    deleteIpFromBlocklist,
    getPlatformCoins,
    createPlatformCoin,
    updatePlatformCoin,
    getAirdropCampaigns,
    getAirdropCampaign,
    createAirdropCampaign,
    previewAirdropCampaign,
    dryRunAirdropCampaign,
    cancelAirdropCampaign,
    getAirdropExecuteConfig,
    executeAirdropCampaign,
    listAdminCryptoWallets,
    getPlatformCoinDeployConfig,
    deployPlatformCoinTestnet,
    getComplianceAlerts,
    assignAlert,
    updateAlertStatus,
    resolveComplianceAlert,
    getProviderConfigs,
    getProviderConfigsByProvider,
    updateProviderConfig,
    getTradingProviderStatus,
    getCurrentTradingProvider,
    setTradingProvider,
    testTradingProviderConnection,
    getAssetTokens,
    createAssetToken,
    updateAssetTokenValue,
    updateAssetTokenStatus,
    getSessionSettings,
    updateSessionSettings,
    getInactivitySettings,
    updateInactivitySettings,
    getSecuritySettings,
    updateSecuritySettings,
    getTransactionCounts,
    getKycSummary,
    getKybSummary,
    getKycSubmissions,
    getKybSubmissions,
    reviewKycSubmission,
    reviewKybSubmission,
    getAvailableProducts,
    // Multi-signature wallet methods
    getMultiSignatureWallets,
    getMultiSignatureWalletById,
    createMultiSignatureWallet,
    createPropertyEscrowWallet,
    activateMultiSignatureWallet,
    freezeMultiSignatureWallet,
    getWalletSignatories,
    getWalletTransactions,
    createWalletTransaction,
    getAvailableUsers,
    // Trading Strategy Reports
    getTradingStrategyDetailedTransactions,
    getTradingStrategySummary,
    getTradingStrategyPnlSummary,
    getTradingStrategyPnlHistory,
    getTradingStrategyComparison,
    getTradingStrategyRecentTransactions,
    getTradingStrategyDetailedTransactionsWithUsers,
    getTradingStrategyVolatilityData,
    getAllTradingStrategies,
    getStrategyReconciliationData,
    reconcileStrategyWithKuCoin,
    fixReconciliationDiscrepancies,
    backfillMissingCloseOrderIds,
    syncClosedTradesFromKuCoinHistory,
    fixHedgePositions,
    getLiveData,
    getStrategyDetails,
    exportTradingStrategyDetailedTransactionsCSV,
    exportTradingStrategySummaryCSV,
    exportTradingStrategyPnlSummaryCSV,
    exportTradingStrategyComparisonCSV,
    exportTradingStrategyDetailedTransactionsPDF,
    exportTradingStrategySummaryPDF,
    exportTradingStrategyPnlSummaryPDF,
    exportTradingStrategyComparisonPDF,
    exportTradingStrategyDetailedTransactionsWithUsersCSV,
    exportTradingStrategyDetailedTransactionsWithUsersPDF,
    exportTradingStrategyRecentTransactionsCSV,
    exportTradingStrategyRecentTransactionsPDF,
    getStrategyLogs,
    closeHedgeLeg,
    closePosition,
    // Admin Personal Trading Accounts
    getMyTradingAccounts,
    getMyTradingAccountById,
    createMyTradingAccount,
    updateMyTradingAccount,
    deleteMyTradingAccount,
    testMyTradingAccount,
    // Stablecoin Config Management
    getStablecoinConfigs,
    createStablecoinConfig,
    updateStablecoinConfig,
    setStablecoinConfigStatus,
    deleteStablecoinConfig,
    // Subscription
    getAdminSubscriptionStatus,
    getAdminSubscriptionPlans,
    startAdminSubscriptionRenewal,
    getAdminSubscriptionRenewPending,
    confirmAdminSubscriptionRenewalPayment,
    submitAdminSubscriptionCryptoPayment,
    payAdminSubscriptionWithWallet
  ]);
}